import React, { useState, useEffect, useMemo } from "react";

// libraries
import { Form, Grid } from "semantic-ui-react";
import Joi from "joi";
import PropTypes from "prop-types";
import { isEmpty, map, isEqual, trim, get, find } from "lodash";

// Components
import Input from "@Components/Input";
import IKModal from "@Components/IkModal";

// Modals
import ConfirmModal from "@Modals/ConfirmModal";

// redux
import { connect } from "react-redux";
import genericAction from "@Redux/Actions/generic";

// Utils
import { stateSetter, getExcludingNulls } from "@Utils";

import typeOptions from "./staticData";

// Styles
import styles from "./styles.module.css";

const UsersCrud = ({ modalCrud, parentsMenus, tableValue, onClose, dispatch, refresh, selectedParentId, setSelectedParentId }) => {
	// modal
	const [open, setOpen] = useState(false);
	const [modalOption, setModalOption] = useState("");

	// form
	const [initialFormData, setInitialFormData] = useState([]);
	const [name, setName] = useState("");
	const [parentId, setParentId] = useState(null);
	const [icon, setIcon] = useState("");
	const [order, setOrder] = useState("");
	const [path, setPath] = useState("");
	const [type, setType] = useState("menu");

	// validation
	const [errors, setErrors] = useState({
		name: "",
		icon: "",
		path: "",
		type: "",
		order: "",
	});

	// validations
	const schema = Joi.object().keys({
		type: Joi.string().valid("divider", "link", "menu").required(),
		name: Joi.string().required().messages({
			"string.empty": "Nombre es un campo requerido",
			"string.min": "Nombre debe tener mínimo 2 caracteres",
		}),
		icon: Joi.string().when("type", {
			is: Joi.not("divider"),
			then: Joi.required().messages({
				"string.empty": "Icono es un campo requerido",
				"string.min": "Icono debe tener mínimo 2 caracteres",
			}),
			otherwise: Joi.string().min(2).allow("").messages({
				"string.min": "Icono debe tener mínimo 2 caracteres",
			}),
		}),
		path: Joi.string().when("type", {
			is: Joi.not("divider"),
			then: Joi.required().messages({
				"string.empty": "Path es un campo requerido",
				"string.min": "Path debe tener mínimo 2 caracteres",
			}),
			otherwise: Joi.string().min(2).allow("").messages({
				"string.min": "Path debe tener mínimo 2 caracteres",
			}),
		}),

		order: Joi.number().integer().required().messages({
			"number.base": "Orden debe ser un número",
			"number.integer": "Orden debe ser un número entero",
			"any.required": "Orden es un campo requerido",
		}),
	});

	// parent placeholder
	const parentPlaceholder = useMemo(() => {
		if (modalOption !== "Add" || !selectedParentId || !parentsMenus.length) return "Elegir";
		const found = find(parentsMenus, { value: selectedParentId });
		return found ? `Último utilizado: ${found.text}` : "Elegir";
	}, [modalOption, selectedParentId, parentsMenus]);

	// useEffect
	useEffect(() => {
		setModalOption(modalCrud);
		const formData = {
			name: getExcludingNulls(tableValue, "name", ""),
			parentId: getExcludingNulls(tableValue, "parentId", null),
			icon: getExcludingNulls(tableValue, "icon", ""),
			path: getExcludingNulls(tableValue, "path", ""),
			type: getExcludingNulls(tableValue, "type", "menu"),
			order: String(getExcludingNulls(tableValue, "order", "")),
		};
		stateHandler(formData);
		setInitialFormData(formData);
	}, [tableValue]);

	// functions
	const stateHandler = (namedData, dataInput) => {
		const setFunctions = {
			name: setName,
			parentId: setParentId,
			icon: setIcon,
			order: setOrder,
			path: setPath,
			type: setType,
		};

		stateSetter(namedData, dataInput, setFunctions, { errors, setErrors });
	};

	const handleTypeChange = (e, { value }) => {
		if (value === "divider") {
			setName("Divider");
		}
		setType(value);
	};

	const handleParentChange = (e, { value }) => {
		setParentId(value);
		if (typeof setSelectedParentId === "function") {
			setSelectedParentId(value);
		}
	};

	const onSuccess = () => {
		if (modalOption === "Add") {
			const clearedData = {
				name: "",
				parentId,
				icon: "",
				order: "",
				path: "",
				type: "menu",
			};
			stateHandler(clearedData);
			setInitialFormData(clearedData);
		} else {
			onClose();
		}
		refresh();
	};

	// handle save
	const handlerSaved = async () => {
		const validationObj = {
			name,
			icon,
			path,
			type,
			order,
		};

		const validation = schema.validate(validationObj, { abortEarly: false });
		if (validation.error) {
			const { details } = validation.error;
			let errorsVal = {
				name: "",
				icon: "",
				path: "",
				type: "",
				order: "",
			};
			map(details, (item) => {
				errorsVal = { ...errorsVal, [item.context.key]: item.message };
			});
			setErrors(errorsVal);
		} else if (modalOption === "Add") {
			const dataEditor = {
				name: trim(name),
				parentId: parseInt(parentId, 10),
				icon: trim(icon),
				order: parseInt(order, 10),
				path: trim(path),
				type: trim(type),
			};
			const menuData = await dispatch(genericAction({ method: "post", route: "menu", body: dataEditor }));
			if (menuData) {
				onSuccess();
			}
		} else {
			let dataEditor = {};
			if (name !== initialFormData.name) dataEditor = { ...dataEditor, name: trim(name) };
			if (parentId !== initialFormData.parentId) dataEditor = { ...dataEditor, parentId: parseInt(parentId, 10) };
			if (icon !== initialFormData.icon) dataEditor = { ...dataEditor, icon: trim(icon) };
			if (order !== initialFormData.order) dataEditor = { ...dataEditor, order: parseInt(order, 10) };
			if (path !== initialFormData.path) dataEditor = { ...dataEditor, path: trim(path) };
			if (type !== initialFormData.type) dataEditor = { ...dataEditor, type: trim(type) };
			const menuData = await dispatch(genericAction({ method: "patch", route: `menu/${tableValue.id}`, body: dataEditor }));
			if (menuData) {
				onSuccess();
			}
		}
	};

	// modal
	const cancelModal = () => {
		onClose();
	};

	const modalOpen = () => {
		if (
			isEqual(name, initialFormData.name) &&
			isEqual(parentId, initialFormData.parentId) &&
			isEqual(icon, initialFormData.icon) &&
			isEqual(order, initialFormData.order) &&
			isEqual(path, initialFormData.path) &&
			isEqual(type, initialFormData.type)
		) {
			cancelModal();
		} else if (open) {
			setOpen(false);
		} else {
			setOpen(true);
		}
	};

	return (
		<IKModal
			title={modalOption === "Add" ? " Agregar Menú" : `Menú: ${get(tableValue, "name", "")} - Id: ${String(tableValue.id)}`}
			size="medium"
			// id={tableValue.id}
			onSuccess={handlerSaved}
			onClose={modalOpen}
			mode={modalOption}
			fullHeight
			setMode={setModalOption}>
			<div className={styles.modalContent}>
				<fieldset className={styles.fieldset}>
					<Grid columns={2}>
						<Grid.Row stretched className={styles.contRow}>
							<Grid.Column>
								<Form autoComplete="off" noValidate>
									<Input
										icon="write"
										required={type !== "divider"}
										label="Nombre"
										name="name"
										error={!isEmpty(errors.name) && { content: errors.name, pointing: "above" }}
										width={14}
										disabled={modalOption === "Watch"}
										value={name}
										onChange={stateHandler}
									/>
									<Input
										icon="hashtag"
										label="Orden"
										required
										name="order"
										width={14}
										disabled={modalOption === "Watch"}
										value={order}
										onChange={stateHandler}
										error={!isEmpty(errors.order) && { content: errors.order, pointing: "above" }}
									/>
									<div className={styles.dropdownContainer}>
										<span className={styles.dropdownLabel}>
											Tipo <span className={styles.required}> *</span>
										</span>
										<Form.Select
											search
											name="type"
											placeholder="Elegir Tipo"
											disabled={modalOption === "Watch"}
											value={type}
											onChange={handleTypeChange}
											options={typeOptions}
											fluid
											upward
										/>
									</div>
									<div className={styles.parentsDropdownContainer}>
										<span className={styles.dropdownLabel}>Padre</span>
										<Form.Select
											search
											clearable
											name="parentId"
											placeholder={parentPlaceholder}
											disabled={modalOption === "Watch"}
											value={parentId}
											onChange={handleParentChange}
											options={parentsMenus}
											fluid
										/>
									</div>
								</Form>
							</Grid.Column>
							<Grid.Column>
								<Form autoComplete="off" noValidate>
									<Input
										icon="image"
										required={type !== "divider"}
										label="Icono"
										name="icon"
										error={!isEmpty(errors.icon) && { content: errors.icon, pointing: "above" }}
										width={14}
										disabled={modalOption === "Watch"}
										value={icon}
										onChange={stateHandler}
									/>
									<Input
										icon="write"
										required={type !== "divider"}
										label="Path"
										name="path"
										error={!isEmpty(errors.path) && { content: errors.path, pointing: "above" }}
										width={14}
										disabled={modalOption === "Watch"}
										value={path}
										onChange={stateHandler}
									/>
								</Form>
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</fieldset>
			</div>
			{open && (
				<ConfirmModal
					buttonLabel="Salir"
					visible
					header="Confirmar Cerrar"
					content="Está seguro que desea salir del formulario"
					onConfirm={cancelModal}
					onDismiss={modalOpen}
				/>
			)}
		</IKModal>
		// <Modal className={styles.modalFirst} size="large" open closeIcon onClose={modalOpen}>
		// 	<Modal.Header className={styles.modalHeader}>
		// 		<div className={styles.modalHeader}>
		// 			{modalOption === "Watch" && <Icon name="eye" />}
		// 			{modalOption === "Edit" && <Icon name="edit" />}
		// 			{modalOption === "Add" && <Icon name="plus" />}
		// 			{modalOption === "Add"
		// 				? " Agregar Menú"
		// 				: `Menú: ${get(tableValue, "name", "")} - Id: ${String(tableValue.id)}`}
		// 		</div>
		// 		<div>
		// 			{modalOption === "Watch" && (
		// 				<Button
		// 					onClick={() => {
		// 						return setModalOption("Edit");
		// 					}}
		// 					className="modal-button-edit">
		// 					<Icon name="edit" />
		// 				</Button>
		// 			)}

		// 			{modalOption === "Edit" && (
		// 				<Button
		// 					onClick={() => {
		// 						return setModalOption("Watch");
		// 					}}
		// 					className="modal-button-watch">
		// 					<Icon name="eye" />
		// 				</Button>
		// 			)}
		// 		</div>
		// 	</Modal.Header>
		// 	<Modal.Content scrolling className={styles.modalContent}>

		// 	</Modal.Content>
		// 	<Modal.Actions className={styles.modalFooter}>
		// 		{modalOption === "Watch" ? (
		// 			<Button className={styles.negativeButton} onClick={modalOpen}>
		// 				Salir
		// 			</Button>
		// 		) : (
		// 			<Button className={styles.negativeButton} onClick={modalOpen}>
		// 				Atrás
		// 			</Button>
		// 		)}
		// 		{modalOption !== "Watch" && (
		// 			<Button className={styles.positiveButton} onClick={handlerSaved}>
		// 				{modalOption === "Add" ? "Guardar" : "Modificar"}
		// 			</Button>
		// 		)}
		// 	</Modal.Actions>
		// 	{open && (
		// 		<ConfirmModal
		// 			buttonLabel="Salir"
		// 			visible
		// 			header="Confirmar"
		// 			content="Está seguro que desea salir del formulario"
		// 			onConfirm={cancelModal}
		// 			onDismiss={modalOpen}
		// 		/>
		// 	)}
		// </Modal>
	);
};

UsersCrud.propTypes = {
	tableValue: PropTypes.object,
	onClose: PropTypes.func,
	dispatch: PropTypes.func,
	refresh: PropTypes.func,
	modalCrud: PropTypes.string,
	parentsMenus: PropTypes.array,
	selectedParentId: PropTypes.number,
	setSelectedParentId: PropTypes.func,
};

function mapStateToProps() {
	return {};
}
export default connect(mapStateToProps)(UsersCrud);
